// wcwb/frontend/src/components/SpoonacularModal.js
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { S } from './styles';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Map party theme keywords to Spoonacular cuisine types
function themeToFilters(theme = '') {
  const t = theme.toLowerCase();
  const cuisineMap = [
    { keys: ['mediterranean','greek','mezze','levantine'], cuisine: 'mediterranean' },
    { keys: ['italian','pasta','pizza','roman'],           cuisine: 'italian' },
    { keys: ['japanese','izakaya','sushi','ramen'],        cuisine: 'japanese' },
    { keys: ['moroccan','tagine','north african'],         cuisine: 'african' },
    { keys: ['mexican','oaxacan','taco','mole'],           cuisine: 'mexican' },
    { keys: ['peruvian','ceviche','latin'],                cuisine: 'latin american' },
    { keys: ['indian','curry','spice'],                    cuisine: 'indian' },
    { keys: ['french','bistro','provencal'],               cuisine: 'french' },
    { keys: ['thai','vietnamese','asian'],                 cuisine: 'asian' },
    { keys: ['american','bbq','burger','southern'],        cuisine: 'american' },
  ];
  for (const entry of cuisineMap) {
    if (entry.keys.some(k => t.includes(k))) return entry.cuisine;
  }
  return '';
}

// Map dish types from Spoonacular to our categories
function dishTypeToCategory(dishTypes = []) {
  const d = dishTypes.map(t => t.toLowerCase());
  if (d.some(t => ['appetizer','starter','antipasti','snack','fingerfood'].includes(t))) return 'Starter';
  if (d.some(t => ['main course','main dish','dinner','lunch'].includes(t))) return 'Main';
  if (d.some(t => ['side dish','side','salad'].includes(t))) return 'Side';
  if (d.some(t => ['dessert','sweet'].includes(t))) return 'Dessert';
  if (d.some(t => ['beverage','drink','cocktail'].includes(t))) return 'Drinks';
  if (d.some(t => ['bread','biscuit'].includes(t))) return 'Bread';
  return 'Main';
}

export default function SpoonacularModal({ onClose, onSelect, eventTheme }) {
  const { token } = useAuth();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError]       = useState('');
  const inputRef = useRef(null);

  const cuisineHint = themeToFilters(eventTheme);

  // Auto-search on mount using theme as initial query
  useEffect(() => {
    if (cuisineHint) doSearch(cuisineHint);
    inputRef.current?.focus();
  }, []);

  async function doSearch(q = query) {
    if (!q.trim()) return;
    setLoading(true); setError(''); setResults([]); setSelected(null);
    try {
      const params = new URLSearchParams({ query: q, number: 12, ...(cuisineHint && { cuisine: cuisineHint }) });
      const res = await fetch(`${API}/spoonacular/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setResults(data.results || []);
      if (!data.results?.length) setError('No recipes found — try a different search term');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function selectRecipe(recipe) {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API}/spoonacular/recipe/${recipe.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const detail = await res.json();
      if (!res.ok) throw new Error(detail.error);
      setSelected(detail);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  function useThisRecipe() {
    if (!selected) return;
    onSelect({
      name:        selected.title,
      category:    dishTypeToCategory(selected.dishTypes),
      description: selected.summary,
      recipe:      [
        selected.ingredients ? `Ingredients: ${selected.ingredients}` : '',
        selected.instructions ? `\n\nMethod:\n${selected.instructions}` : '',
        selected.sourceUrl ? `\n\nSource: ${selected.sourceUrl}` : '',
      ].join('').trim(),
    });
    onClose();
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:24 }}>Find a recipe</h3>
            {cuisineHint && (
              <p style={{ fontSize:12, color:'#C4612A', marginTop:4 }}>
                🎨 Filtered for {cuisineHint} cuisine based on your party theme
              </p>
            )}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#7A6555' }}>×</button>
        </div>

        {/* Search bar */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          <input
            ref={inputRef}
            style={{ ...S.input, marginBottom:0, flex:1 }}
            placeholder="Search recipes e.g. lamb kofta, spanakopita…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
          />
          <button style={{ ...S.btnPrimary, flexShrink:0 }} onClick={() => doSearch()} disabled={loading}>
            {loading ? '…' : 'Search'}
          </button>
        </div>

        {error && (
          <div style={{ background:'#FFEBEE', color:'#B71C1C', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>{error}</div>
        )}

        {/* Detail view */}
        {selected && (
          <div style={{ background:'#FAF7F2', borderRadius:14, padding:20, marginBottom:16 }}>
            <div style={{ display:'flex', gap:14, marginBottom:14 }}>
              {selected.image && (
                <img src={selected.image} alt={selected.title}
                  style={{ width:80, height:80, borderRadius:10, objectFit:'cover', flexShrink:0 }}/>
              )}
              <div style={{ flex:1 }}>
                <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, marginBottom:4 }}>{selected.title}</h4>
                <div style={{ display:'flex', gap:10, fontSize:12, color:'#7A6555' }}>
                  {selected.readyInMinutes && <span>⏱ {selected.readyInMinutes} min</span>}
                  {selected.servings && <span>👥 {selected.servings} servings</span>}
                </div>
                {selected.diets?.length > 0 && (
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:6 }}>
                    {selected.diets.map(d => (
                      <span key={d} style={{ fontSize:10, padding:'2px 6px', borderRadius:8, background:'#E8F5E9', color:'#1B5E20' }}>{d}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {selected.summary && (
              <p style={{ fontSize:13, color:'#5A3E2B', lineHeight:1.55, marginBottom:14 }}>{selected.summary}</p>
            )}
            {selected.ingredients && (
              <div style={{ marginBottom:12 }}>
                <p style={{ fontSize:11, fontWeight:500, letterSpacing:'.07em', textTransform:'uppercase', color:'#7A6555', marginBottom:4 }}>Ingredients</p>
                <p style={{ fontSize:13, color:'#5A3E2B', lineHeight:1.6 }}>{selected.ingredients}</p>
              </div>
            )}
            <div style={{ display:'flex', gap:8 }}>
              <button style={{ ...S.btnGhost, fontSize:13, padding:'7px 14px' }} onClick={() => setSelected(null)}>← Back</button>
              <button style={{ ...S.btnPrimary, flex:1 }} onClick={useThisRecipe}>Use this recipe →</button>
            </div>
          </div>
        )}

        {/* Results grid */}
        {!selected && (
          <div style={{ maxHeight:420, overflowY:'auto' }}>
            {loading && (
              <div style={{ textAlign:'center', padding:'40px 0', color:'#7A6555', fontSize:14 }}>
                Searching recipes…
              </div>
            )}
            {loadingDetail && (
              <div style={{ textAlign:'center', padding:'40px 0', color:'#7A6555', fontSize:14 }}>
                Loading recipe details…
              </div>
            )}
            {!loading && !loadingDetail && results.length === 0 && !error && (
              <div style={{ textAlign:'center', padding:'40px 0', color:'#7A6555', fontSize:14 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🍽️</div>
                Search for a recipe above{cuisineHint ? ` or browse ${cuisineHint} cuisine` : ''}
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {results.map(r => (
                <div key={r.id}
                  onClick={() => selectRecipe(r)}
                  style={{ borderRadius:12, border:'1px solid #E5D9C8', overflow:'hidden', cursor:'pointer', background:'white', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#C4612A'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(196,97,42,.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5D9C8'; e.currentTarget.style.boxShadow = 'none'; }}>
                  {r.image && (
                    <img src={r.image} alt={r.title}
                      style={{ width:'100%', height:110, objectFit:'cover' }}/>
                  )}
                  <div style={{ padding:'10px 12px' }}>
                    <p style={{ fontFamily:'Cormorant Garamond,serif', fontSize:15, marginBottom:4, lineHeight:1.3 }}>{r.title}</p>
                    <div style={{ display:'flex', gap:8, fontSize:11, color:'#7A6555' }}>
                      {r.readyInMinutes && <span>⏱ {r.readyInMinutes}m</span>}
                      {r.diets?.slice(0,1).map(d => <span key={d} style={{ background:'#E8F5E9', color:'#1B5E20', padding:'1px 5px', borderRadius:6 }}>{d}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay = { position:'fixed', inset:0, background:'rgba(44,26,14,.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:16 };
const modal   = { background:'white', borderRadius:24, width:'100%', maxWidth:600, maxHeight:'92vh', overflowY:'auto', padding:32 };
